import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Skeleton } from '../../lib/shadcn/skeleton'
import { Hourglass } from 'lucide-react'
import { cn } from '../../lib/shadcn/utils'

export interface WaitingOrder {
  order_id: string
  customer: string
  order_date: string
  age_days: number
  bags_ordered: number
  bags_picked: number
}

const AGE_BUCKETS: { label: string; min: number; max: number }[] = [
  { label: '0–3 days', min: 0, max: 3 },
  { label: '4–7 days', min: 4, max: 7 },
  { label: '8–14 days', min: 8, max: 14 },
  { label: '15+ days', min: 15, max: Infinity },
]

const LIST_LIMIT = 6

function stageLabel(o: WaitingOrder): string {
  if (o.bags_ordered > 0 && o.bags_picked >= o.bags_ordered) return 'Packed'
  if (o.bags_picked > 0) return `Picked ${o.bags_picked} of ${o.bags_ordered}`
  return 'Not picked'
}

// Orders still waiting to go out, by how long they've been waiting. Replaces
// the Order Status pie, which mostly showed lifetime shipped vs delivered.
export default function OrdersWaitingCard({ data, loading }: { data: WaitingOrder[]; loading: boolean }) {
  const buckets = useMemo(
    () =>
      AGE_BUCKETS.map(b => ({
        ...b,
        count: data.filter(o => o.age_days >= b.min && o.age_days <= b.max).length,
      })),
    [data]
  )
  const totalBags = useMemo(() => data.reduce((sum, o) => sum + Math.max(o.bags_ordered - o.bags_picked, 0), 0), [data])
  const oldest = useMemo(() => [...data].sort((a, b) => b.age_days - a.age_days).slice(0, LIST_LIMIT), [data])

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Hourglass className="w-4 h-4" />
          Orders waiting
        </CardTitle>
        <CardDescription>Open orders not yet shipped or delivered, by days since the order was placed</CardDescription>
        {!loading && data.length > 0 && (
          <p className="text-sm pt-1">
            <span className="text-2xl font-semibold tabular-nums">{data.length}</span>{' '}
            <span className="text-muted-foreground">
              order{data.length === 1 ? '' : 's'} · {totalBags.toLocaleString()} bags still to pick
            </span>
          </p>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-[260px] w-full" />
        ) : data.length === 0 ? (
          <p className="py-14 text-sm text-muted-foreground text-center px-4">
            Nothing waiting. Every order has gone out.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-2">
              {buckets.map((b, i) => (
                <div
                  key={b.label}
                  className={cn(
                    'rounded-md border px-2 py-2 text-center',
                    i === AGE_BUCKETS.length - 1 && b.count > 0 && 'border-destructive/50 bg-destructive/5'
                  )}
                >
                  <div className="text-xl font-semibold tabular-nums">{b.count}</div>
                  <div className="text-xs text-muted-foreground whitespace-nowrap">{b.label}</div>
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Waiting longest</p>
              <ul className="divide-y">
                {oldest.map(o => (
                  <li key={o.order_id}>
                    <Link
                      to={`/orders?order=${encodeURIComponent(o.order_id)}`}
                      className="flex items-center gap-3 py-1.5 text-sm hover:bg-muted/50 rounded-sm px-1 -mx-1"
                    >
                      <span className="w-14 shrink-0 text-right font-semibold tabular-nums">
                        {o.age_days}d
                      </span>
                      <span className="min-w-0 flex-1 truncate">
                        {o.customer || 'Unknown customer'}
                        <span className="text-muted-foreground"> · {o.order_id}</span>
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground">{stageLabel(o)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              {data.length > LIST_LIMIT && (
                <Link to="/orders" className="inline-block mt-2 text-sm font-medium underline underline-offset-2">
                  See all {data.length} on Orders
                </Link>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
